import React from "react";
import { Link } from "react-router-dom";
import { Ticket, ShieldCheck, Zap, ArrowRight, CheckCircle2, Check, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LOGO_HORIZONTAL } from "@/lib/brand";

const WA_SUPPORT = "https://wa.me/50664096004";

const PLANS = [
  { key: "free", name: "Free", price: "Gratis", note: "Para siempre", highlight: false,
    features: ["1 rifa activa", "100 números por rifa", "Comprobantes de pago", "Página pública + WhatsApp", "Sorteo digital verificable"] },
  { key: "full", name: "Full", price: "₡6.950", note: "por mes", highlight: false,
    features: ["3 rifas activas", "2.000 números por rifa", "3 asistentes", "5 fotos por evento", "Exportar clientes (CSV)", "Historial y reportes"] },
  { key: "pro", name: "Pro", price: "₡14.950", note: "por mes", highlight: true,
    features: ["5 rifas activas", "9.000 números por rifa", "5 asistentes", "10 fotos por evento", "Tickets con QR de validación", "Estadísticas avanzadas", "Marca blanca (sin MCTECH)", "Soporte prioritario"] },
  { key: "custom", name: "Custom", price: "A tu medida", note: "", highlight: false,
    features: ["Rifas ilimitadas", "Números a tu medida", "Asistentes ilimitados", "Fotos a tu medida", "Todo lo del plan Pro", "Cuenta gerenciada"] },
];

const COMPARE = [
  { label: "Rifas activas", vals: ["1", "3", "5", "Ilimitadas"] },
  { label: "Números por rifa", vals: ["100", "2.000", "9.000", "A medida"] },
  { label: "Asistentes / vendedores", vals: ["0", "3", "5", "Ilimitados"] },
  { label: "Fotos por evento", vals: ["3", "5", "10", "A medida"] },
  { label: "Comprobantes de pago", vals: [true, true, true, true] },
  { label: "Página pública + WhatsApp", vals: [true, true, true, true] },
  { label: "Sorteo digital verificable", vals: [true, true, true, true] },
  { label: "Exportar clientes (CSV)", vals: [false, true, true, true] },
  { label: "Historial y reportes", vals: [false, true, true, true] },
  { label: "Estadísticas avanzadas", vals: [false, false, true, true] },
  { label: "Marca blanca", vals: [false, false, true, true] },
  { label: "Soporte", vals: ["Comunidad", "Email", "Prioritario", "Gerenciado"] },
];

function Cell({ v, highlight }) {
  if (v === true) return <Check size={17} className={`mx-auto ${highlight ? "text-[#00AEEF]" : "text-emerald-500"}`} />;
  if (v === false) return <Minus size={16} className="mx-auto text-neutral-300" />;
  return <span className={`text-sm font-semibold ${highlight ? "text-[#00AEEF]" : "text-neutral-700"}`}>{v}</span>;
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-neutral-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_HORIZONTAL} alt="MCTECH EVENTOS" className="h-7 sm:h-8 w-auto" style={{ mixBlendMode: "multiply" }} />
            <span className="font-display font-extrabold text-lg tracking-tight hidden sm:inline">EVENTOS</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <Link to="/login"><Button variant="outline" data-testid="nav-login-btn" className="rounded-xl border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white text-xs px-2.5 h-8 sm:text-sm sm:px-4 sm:h-9 whitespace-nowrap">Soy organizador</Button></Link>
            <Link to="/login"><Button data-testid="nav-cta-btn" className="bg-[#00AEEF] hover:bg-[#0092CC] rounded-xl text-xs px-2.5 h-8 sm:text-sm sm:px-4 sm:h-9 whitespace-nowrap">Empezar gratis</Button></Link>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-4 pt-16 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#0092CC] bg-[#E6F7FE] px-3 py-1.5 rounded-full">
              <Zap size={14} /> Eventos de números en línea
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none mt-6">
              Crea y gestiona tus <span className="text-[#00AEEF]">eventos</span> como un profesional
            </h1>
            <p className="text-lg text-neutral-600 mt-6 leading-relaxed">
              Vende números, verifica pagos y ejecuta el evento desde un solo lugar. Tus compradores participan cómodamente desde el celular.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/login"><Button size="lg" data-testid="hero-cta-btn" className="bg-[#00AEEF] hover:bg-[#0092CC] rounded-full h-12 px-7 text-base hover:scale-[1.02] active:scale-95 transition-transform">Empezar gratis <ArrowRight size={18} className="ml-2" /></Button></Link>
              <Link to="/login"><Button size="lg" variant="outline" data-testid="hero-organizer-btn" className="rounded-full h-12 px-7 text-base border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white">Soy organizador</Button></Link>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8 text-sm text-neutral-600">
              {["Cuenta regresiva en vivo", "Verificación de comprobantes", "Evento aleatorio verificable"].map((f) => (
                <span key={f} className="flex items-center gap-1.5"><CheckCircle2 size={16} className="text-[#00AEEF]" />{f}</span>
              ))}
            </div>
          </div>
          <div className="animate-fade-up flex items-center justify-center min-h-[420px]" style={{ animationDelay: "0.1s" }}>
            <img src="/mctech-logo.png" alt="MCTECH EVENTOS" className="w-full max-w-md object-contain mx-auto" />
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-24">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Ticket, t: "Eventos a la medida", d: "Configura cantidad de números, precios, paquetes con descuento y reglas propias." },
            { icon: ShieldCheck, t: "Pagos verificados", d: "Cola de comprobantes con aprobar/rechazar y correos automáticos al comprador." },
            { icon: Zap, t: "Evento transparente", d: "Aleatorio con semilla verificable, manual o vinculado a la lotería nacional." },
          ].map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={c.t} className="bg-white rounded-2xl border border-neutral-200 p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
                <div className="w-11 h-11 rounded-xl bg-[#E6F7FE] text-[#00AEEF] flex items-center justify-center mb-4"><Icon size={22} /></div>
                <h3 className="font-display font-bold text-lg">{c.t}</h3>
                <p className="text-neutral-600 mt-2 text-sm leading-relaxed">{c.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing */}
      <section id="planes" className="max-w-6xl mx-auto px-4 pb-16" data-testid="pricing-section">
        <div className="text-center mb-10">
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Planes para cada organizador</h2>
          <p className="text-neutral-500 mt-2 text-base">Empieza gratis y mejora cuando lo necesites. Sin permanencia.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {PLANS.map((p) => (
            <div key={p.key} data-testid={`plan-card-${p.key}`}
              className={`rounded-2xl p-6 flex flex-col ${p.highlight ? "bg-neutral-900 text-white shadow-2xl ring-2 ring-[#00AEEF] lg:scale-[1.03]" : "bg-white border border-neutral-200 shadow-[0_8px_32px_rgba(0,0,0,0.04)]"}`}>
              {p.highlight && <span className="self-start text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#00AEEF] text-white mb-3">Más popular</span>}
              <p className={`text-xs font-bold uppercase tracking-[0.2em] ${p.highlight ? "text-[#00AEEF]" : "text-neutral-400"}`}>{p.name}</p>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="font-display font-extrabold text-3xl">{p.price}</span>
                <span className={`text-xs ${p.highlight ? "text-neutral-400" : "text-neutral-400"}`}>{p.note}</span>
              </div>
              <ul className="mt-5 space-y-2.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check size={16} className={`mt-0.5 shrink-0 ${p.highlight ? "text-[#00AEEF]" : "text-emerald-500"}`} />
                    <span className={p.highlight ? "text-neutral-200" : "text-neutral-600"}>{f}</span>
                  </li>
                ))}
              </ul>
              {p.key === "free" ? (
                <Link to="/login" className="mt-6">
                  <Button data-testid="plan-cta-free" className="w-full rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white">
                    Empezar gratis <ArrowRight size={16} className="ml-1" />
                  </Button>
                </Link>
              ) : (
                <a href={WA_SUPPORT} target="_blank" rel="noreferrer" className="mt-6">
                  <Button data-testid={`plan-cta-${p.key}`} className={`w-full rounded-xl ${p.highlight ? "bg-[#00AEEF] hover:bg-[#0092CC] text-white" : "bg-white border border-neutral-300 text-neutral-900 hover:bg-neutral-50"}`}>
                    Contáctanos <ArrowRight size={16} className="ml-1" />
                  </Button>
                </a>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Comparison table */}
      <section className="max-w-6xl mx-auto px-4 pb-24" data-testid="pricing-compare">
        <div className="text-center mb-8">
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight">Comparativa de planes</h2>
          <p className="text-neutral-500 mt-2 text-sm">Todo lo que incluye cada plan, de un vistazo</p>
        </div>
        <div className="rounded-2xl border border-neutral-200 bg-white shadow-[0_8px_32px_rgba(0,0,0,0.04)] overflow-x-auto hidden sm:block">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-neutral-100">
                <th className="text-left font-medium text-neutral-400 px-5 py-4">Característica</th>
                {PLANS.map((p) => (
                  <th key={p.key} className={`px-3 py-4 text-center ${p.highlight ? "bg-neutral-900 text-white rounded-t-xl" : ""}`}>
                    <div className="font-display font-bold">{p.name}</div>
                    <div className={`text-xs font-normal ${p.highlight ? "text-[#00AEEF]" : "text-neutral-400"}`}>{p.price}{p.key !== "free" && p.key !== "custom" ? "/mes" : ""}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE.map((row, ri) => (
                <tr key={row.label} className={ri % 2 ? "bg-neutral-50/60" : ""}>
                  <td className="px-5 py-3 text-neutral-600">{row.label}</td>
                  {row.vals.map((v, ci) => (
                    <td key={ci} className={`px-3 py-3 text-center ${PLANS[ci].highlight ? "bg-neutral-900/[0.03]" : ""}`}>
                      <Cell v={v} highlight={PLANS[ci].highlight} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* En móvil, un bloque por plan: se lee sin desplazamiento horizontal */}
        <div className="sm:hidden space-y-3" data-testid="pricing-compare-mobile">
          {PLANS.map((p, ci) => (
            <div key={p.key} data-testid={`pricing-card-${p.key}`}
              className={`rounded-2xl border p-4 ${p.highlight ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 bg-white"}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-display font-bold text-base">{p.name}</span>
                <span className={`text-sm font-semibold ${p.highlight ? "text-[#00AEEF]" : "text-neutral-500"}`}>
                  {p.price}{p.key !== "free" && p.key !== "custom" ? "/mes" : ""}
                </span>
              </div>
              <ul className={`mt-3 space-y-2 text-sm ${p.highlight ? "text-neutral-200" : "text-neutral-600"}`}>
                {COMPARE.map((row) => (
                  <li key={row.label} className={`flex items-center justify-between gap-3 pt-2 border-t first:border-0 first:pt-0 ${p.highlight ? "border-white/10" : "border-neutral-100"}`}>
                    <span className="min-w-0">{row.label}</span>
                    <span className="shrink-0 text-right"><Cell v={row.vals[ci]} highlight={p.highlight} /></span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-neutral-200 py-8 px-4" data-testid="landing-footer">        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-neutral-500">
          <span>© {new Date().getFullYear()} MCTECH EVENTOS</span>
          <Link to="/terminos" data-testid="footer-terms-link" className="font-medium text-neutral-600 hover:text-[#0092CC] underline">Términos y Condiciones</Link>
        </div>
      </footer>
    </div>
  );
}
